<template>
  <div v-if="pageContent?.currentPage === 'advicePracticesPage'" id="advice-practices">
    <Breadcrumbs current-page-name="Advice Practices" />
    <LandingPageModule
      :landing-page-content="pageContent.advicePracticesHero"
      :show-button="true"
      background-color="blue-5"
      class="items-center"
      @button-clicked="showContactUsModal">
      <template #svgComponent>
        <img
          :src="FinancialAdviceSvg"
          alt="financial advice"
          class="max-w-[300px] md:max-w-[25%] mx-auto"
          data-test="financialAdviceImage" />
      </template>
    </LandingPageModule>
    <RegularCurve class="bg-blue-5 h-6 w-full" fill="primary" />
    <div class="bg-primary text-white">
      <div
        class="md:py-mobileNavBarHeight m-auto md:w-[1500px] flex flex-col md:flex-row justify-center items-center gap-14">
        <img
          alt="fin-advice-phone-image"
          class="h-[225px] md:h-[340px] w-[245px] md:w-[380px]"
          src="@/assets/img/fin-advice-phone.png" />
        <div class="flex flex-col justify-center p-6 md:p-6">
          <h3 class="md:w-[600px]" v-html="pageContent.advicePracticesImageAndText.heading" />
          <div class="intro-1 mt-[30px] md:w-[670px]">
            {{ pageContent.advicePracticesImageAndText.subHeading }}
          </div>
          <div class="intro-1 font-bold! mt-[30px]">
            {{ pageContent.advicePracticesImageAndText.body }}
          </div>
        </div>
      </div>
    </div>
    <RegularCurve class="bg-primary h-6 w-full" fill="white" />
    <USPs
      :icons="uspsIcons"
      :usp-data="pageContent.advicePracticesUsps"
      class="py-[60px] bg-white"
      @button-clicked="showContactUsModal">
      <template #banner-slot>
        <div class="px-5 mx-auto">
          <div
            class="hidden lg:grid h-40 xl:h-32 mx-auto mt-20 w-full max-w-[1200px] grid-flow-col auto-cols-auto items-center overflow-hidden rounded-full">
            <div class="flex h-full items-center bg-blue-5">
              <div class="flex h-full pl-10 py-5 pr-5 items-center text-3xl text-secondary">
                10%
              </div>
              <div class="h-full py-4 items-center hidden lg:flex">
                Current Australians with access to face-to-face advice
              </div>
              <div class="h-full">
                <svg
                  class="h-full w-auto bg-primary fill-blue-5"
                  fill="none"
                  viewBox="0 0 57 103"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M0 0H8.64787C13.8137 0 18.6992 2.34887 21.9253 6.3835L56.0026 49.002C57.1705 50.4626 57.1705 52.5374 56.0026 53.998L21.9253 96.6165C18.6992 100.651 13.8137 103 8.64787 103H0V0Z" />
                </svg>
              </div>
            </div>
            <div class="flex h-full items-center bg-primary text-white">
              <div class="flex h-full p-5 items-center text-3xl">10%</div>
              <div class="h-full py-4 items-center hidden lg:flex">
                Hybrid face-to-face and Otivo advice to reach more clients
              </div>
              <div class="h-full">
                <svg
                  class="h-full w-auto bg-blue-1 fill-primary"
                  fill="none"
                  viewBox="0 0 57 103"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M0 0H8.64787C13.8137 0 18.6992 2.34887 21.9253 6.3835L56.0026 49.002C57.1705 50.4626 57.1705 52.5374 56.0026 53.998L21.9253 96.6165C18.6992 100.651 13.8137 103 8.64787 103H0V0Z" />
                </svg>
              </div>
            </div>
            <div class="flex h-full items-center bg-blue-1 text-white">
              <div class="flex h-full p-5 items-center text-3xl">80%</div>
              <div class="h-full py-4 pr-10 items-center hidden lg:flex">
                Partnering with Otivo means never turning clients away.
              </div>
            </div>
          </div>
        </div>
      </template>
    </USPs>
    <div class="bg-grey-1 flex flex-col py-[60px] align-center text-white relative">
      <div class="m-auto px-6 xl:pl-80 relative">
        <img
          alt="looking at computer"
          class="hidden xl:block absolute right-[660px] w-[760px] -bottom-[260px] z-0"
          src="@/assets/img/looking-at-computer.png" />
        <div class="headline-2 xl:w-[620px]">“{{ pageContent.fordScottTestimonial.heading }}”</div>
        <div class="headline-5 mt-[30px] xl:w-[650px]">
          "{{ pageContent.fordScottTestimonial.subHeading }}"
        </div>
        <div class="xl:flex justify-between align-bottom mt-[60px]">
          <div class="paragraph-2 mt-4">Tim Scott, Managing Director of Ford Scott</div>
          <img
            alt="ford scott quote"
            class="w-[110px] mt-[10px] xl:mt-0"
            src="@/assets/img/fordscott.png" />
        </div>
      </div>
    </div>
    <HowItWorksModule
      :how-it-works="pageContent.advicePracticesHowItWorks"
      class="bg-blue-5 pt-[60px] z-10 relative" />
    <Awards class="bg-blue-5 py-20 hidden md:block fill-grey-1 text-grey-1" />
    <RegularCurve :rotate="true" class="bg-primary h-6 w-full" fill="blue-5" />
    <BreathingPhoneSection
      :content="pageContent.breathingPhoneComponent"
      class="bg-primary z-500"
      @button-clicked="showContactUsModal" />
    <ThreeTileDisplay
      :icon-boxes="pageContent.threeTileDisplay.iterableItems"
      class="bg-white z-100" />
    <RegularCurve class="h-6 w-full md:hidden bg-white fill-tertiary" fill="tertiary" />
    <HowWeWorkItOutModal v-if="showHowWeWorkItOutModal" @close="showHowWeWorkItOutModal = false" />
  </div>
</template>

<script lang="ts" setup>
import Awards from '@/components/Footer/Awards.vue'
import Breadcrumbs from '@/components/Breadcrumbs/Breadcrumbs.vue'
import HowItWorksModule from '@/components/HowItWorksModule/HowItWorksModule.vue'
import HowWeWorkItOutModal from '@/components/BrandComponents/Otivo/HowWeWorkItOutModal.vue'
import LandingPageModule from '@/components/BrandComponents/LandingPageModule.vue'
import ThreeTileDisplay from '@/components/BrandComponents/Otivo/ThreeTileDisplay.vue'
import USPs from '@/components/BrandComponents/Otivo/USPs.vue'
import FinancialAdviceSvg from '@/assets/img/finadvice.webp'
import { computed, onBeforeMount, ref } from 'vue'
import { useStore } from 'vuex'

import BankSVG from '@/components/SVGS/BrandIcons/16px/bank.svg'
import IncomeSVG from '@/components/SVGS/BrandIcons/16px/income-and-expenses.svg'
import CashflowSVG from '@/components/SVGS/BrandIcons/16px/cashflow.svg'
import PartnersSVG from '@/components/SVGS/BrandIcons/16px/partners.svg'
import BriefingSVG from '@/components/SVGS/BrandIcons/16px/premium-briefing-plan.svg'
import PapersSVG from '@/components/SVGS/BrandIcons/16px/media-newspaper.svg'
import RegularCurve from '@/components/Shapes/RegularCurve.vue'
import BreathingPhoneSection from '@/components/BreathingPhoneSection.vue'
import GorgiasContactForm from '@/components/BrandComponents/ContactUs/GorgiasContactForm.vue'
import { useModalStore } from '@/store/pinia/ModalStore.ts'
import { useHead } from '@unhead/vue'
import {
  addStructuredData,
  createBreadcrumbSchema,
  createOrganizationSchema,
  createStandardMeta,
} from '@/utils/seo'
import { createServiceSchema } from '@/utils/schemas'

const store = useStore()

// SEO for advice practices service page
const seoData = createStandardMeta({
  title: 'Financial Advice Solutions for Practices | Otivo',
  description:
    'Partner with Otivo to offer digital advice solutions for your financial practice. Improve efficiency, serve more clients, and grow your business.',
  keywords: 'financial advice, digital solutions, advice practices, australia, otivo',
})

const structuredData = addStructuredData([
  createOrganizationSchema(),
  createServiceSchema({
    name: 'Digital Advice Solutions for Financial Practices',
    description:
      'Partner with Otivo to enhance your financial advice practice with our digital solutions. Help your clients with personalised investment and superannuation advice while scaling your practice efficiently.',
  }),
  createBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Services', url: '/services' },
    { name: 'Advice Practices' },
  ]),
])

useHead({
  ...seoData,
  ...structuredData,
})

onBeforeMount(() => store.dispatch('ContentStore/getContent', 'advicePracticesPage'))
const pageContent = computed(() => store.getters['ContentStore/pageContent'])
const showHowWeWorkItOutModal = ref(false)
const showContactUsModal = () => {
  useModalStore().openModal(GorgiasContactForm, {}, 'large', false, {
    backgroundColor: 'white !important',
    padding: '20px',
  })
}
const uspsIcons: Array<unknown> = [
  IncomeSVG,
  BankSVG,
  CashflowSVG,
  PartnersSVG,
  BriefingSVG,
  PapersSVG,
]
</script>
